<script lang="ts">
  import type { FormField } from '../types';
  import { resetField } from '$lib/helpers/form-field-reset';

  export let field: FormField;
  export let inputValue: string = '';
  export let isError: boolean = false;
  export let marketField: boolean = false;

  let group: any[] = [];

  field.selectOptions.forEach((option) => {
    if (option.selected) {
      group.push(option.value);
    }
  });

  $: if (group) {
    inputValue = group.join(', ');
  }
</script>

<div class="CheckboxesField" class:isError class:marketField>
  <input type="hidden" id={field.id} name={field.id} bind:value={inputValue} />
  <fieldset>
    {#if field.label}
      <legend>{field.label}</legend>
    {/if}
    {#each field.selectOptions as option}
      <input
        class="fs-exclude"
        id={`${field.id}_${option.value}`}
        type="checkbox"
        value={option.value}
        use:resetField
        bind:group
      />
      {#if option.label}
        <label class="CheckboxesFieldLabel" for={`${field.id}_${option.value}`}>{option.label}</label>
      {/if}
    {/each}
  </fieldset>
</div>

<style lang="scss">
  @layer standard-components {
    div {
      --form-border-radius: 5px;
      --form-option-icon-size: 16px;
      --form-option-icon-top: calc((var(--line-height-28) - var(--form-option-icon-size)) / 2);
      --form-option-icon-left: 0px;
      --form-field-border-active-width: 2px;
      --color-form-field-border-rgb-triplet: 115, 115, 115;
      --form-field-border-width: 1px;
      --form-check-inner-width: 8px;
      --form-check-inner-height: 5px;
      --form-check-offset-top: 5px;
      --form-check-offset-left: 4px;
      --form-check-stroke-width: 2px;
      --font-weight-base: 400;
      --font-weight-regular: 400;
      --font-weight-medium: 500;
      --form-radio-inner-size: 4px;
      --form-radio-offset: calc((var(--form-option-icon-size) - var(--form-radio-inner-size)) / 2);

      input {
        appearance: none;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        min-width: 0;
        max-width: none;
        min-height: 0;
        max-height: none;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        font-weight: inherit;
        color: inherit;
        background-color: transparent;
        border: none;
        outline: none;
        box-shadow: none;
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .CheckboxesFieldLabel {
        position: relative;
        min-height: var(--line-height-28);
        min-width: var(--line-height-28);
        margin: 0;
        padding-left: calc(var(--form-option-icon-size) + var(--space-10));
        font-size: var(--font-size-16);
        line-height: var(--line-height-28);
        font-weight: var(--font-weight-base);
        color: inherit;
        cursor: pointer;
        display: block;
        width: 100%;
        transition:
          color var(--duration-quick) var(--easing-sharp),
          opacity var(--duration-quick) var(--easing-sharp);

        &::before {
          border-radius: var(--form-border-radius);
          display: block;
          top: calc(var(--form-option-icon-top));
          left: var(--form-option-icon-left);
          width: var(--form-option-icon-size);
          height: var(--form-option-icon-size);
          background-color: white;
          box-shadow:
            inset 0 0 0 2px RGBA(115, 115, 115, 0),
            inset 0 0 0 1px RGB(115, 115, 115);
          transition:
            box-shadow var(--duration-quick) var(--easing-sharp),
            background-color var(--duration-quick) var(--easing-sharp);
          font-size: var(--font-size-16);
          line-height: var(--line-height-28);
          font-weight: var(--font-weight-base);
          color: inherit;
          cursor: pointer;
          content: ' ';
          position: absolute;
          pointer-events: none;
        }

        &::after {
          width: var(--form-check-inner-width);
          height: var(--form-check-inner-height);
          top: calc(var(--form-option-icon-top) + var(--form-check-offset-top) - 1px);
          left: calc(var(--form-option-icon-left) + var(--form-check-offset-left));
          border-style: solid;
          border-color: white;
          border-width: 0;
          border-left-width: var(--form-check-stroke-width);
          border-bottom-width: var(--form-check-stroke-width);
          opacity: 0;
          transform: rotate(-45deg) scale(0);
          transition:
            opacity var(--duration-quick) var(--easing-sharp),
            transform var(--duration-quick) var(--easing-sharp);
          content: ' ';
          position: absolute;
          pointer-events: none;
          font-size: var(--font-size-16);
          line-height: var(--line-height-28);
          font-weight: var(--font-weight-base);
          color: inherit;
          cursor: pointer;
        }
      }

      input[type='checkbox']:checked + label::before {
        background-color: var(--color-blue);
        box-shadow:
          inset 0 0 0 2px RGBA(0, 106, 255, 0),
          inset 0 0 0 2px RGB(0, 106, 255);
      }

      input[type='checkbox']:focus + label::before,
      input[type='checkbox']:active + label::before,
      input[type='checkbox']:checked + label::before {
        box-shadow:
          inset 0 0 0 var(--form-field-border-active-width) RGB(0, 106, 255),
          inset 0 0 0 var(--form-field-border-width) RGBA(0, 106, 255, 0);
      }

      input[type='checkbox']:checked + label::after {
        opacity: 1;
        transform: rotate(-45deg) scale(1);
      }

      &.isError {
        input {
          box-shadow: inset 0 0 0 1px var(--color-red);
          // TODO: Use SVG
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZyBjbGlwLXBhdGg9InVybCgjdHJpYW5nbGUtd2FybmluZy1yZWRfc3ZnX19jbGlwMCkiIGZpbGw9IiNkZjMzMjAiPgogICAgPHBhdGggZD0iTTIyLjgxMyAxOS4xMDVsLS44Ni41MDkuMDA4LjAxMy44NTItLjUyMnpNMTMuMDY1IDIuNjE2bC44Ni0uNTA5YTMuOTk1IDMuOTk1IDAgMCAwLS4wMDgtLjAxM2wtLjg1Mi41MjJ6bS0yLjEzOSAwbC44NjEuNTA5YTEuMDMgMS4wMyAwIDAgMCAuMDItLjAzN2wtLjg4LS40NzJ6TTEuMTc4IDE5LjEwNWwuODUzLjUyMmEuNjguNjggMCAwIDAgLjAwOC0uMDEzbC0uODYxLS41MXpNMTEgMTNhMSAxIDAgMSAwIDIgMGgtMnptMi00YTEgMSAwIDEgMC0yIDBoMnpNMi4yNDcgMjJoMTkuNDk3di0ySDIuMjQ3djJ6bTE5LjQ5NyAwYzEuODA3IDAgMi44MjMtMS45NDcgMS45MjItMy40MThsLTEuNzA2IDEuMDQ1YS4yNC4yNCAwIDAgMSAuMDA5LjI1LjIzMi4yMzIgMCAwIDEtLjA4Mi4wODguMjY2LjI2NiAwIDAgMS0uMTQzLjAzNXYyem0xLjkzLTMuNDA0TDEzLjkyNiAyLjEwN2wtMS43MjIgMS4wMTggOS43NDggMTYuNDg5IDEuNzIyLTEuMDE4ek0xMy45MTggMi4wOTRjLS44NjYtMS40MTQtMy4wMzItMS41Mi0zLjg3My4wNWwxLjc2My45NDRhLjE0OC4xNDggMCAwIDEgLjA2LS4wNjMuMjE1LjIxNSAwIDAgMSAuMTA0LS4wMjVjLjA5MSAwIC4xODIuMDQzLjI0LjEzOGwxLjcwNi0xLjA0NHptLTMuODUyLjAxM0wuMzE3IDE4LjU5NmwxLjcyMiAxLjAxOCA5Ljc0OC0xNi40OS0xLjcyMS0xLjAxN3pNLjMyNiAxOC41ODJDLS42IDIwLjA5MS41NDUgMjIgMi4yNDYgMjJ2LTJjLS4wNzEgMC0uMTUzLS4wMzgtLjIwOS0uMTM2LS4wNTMtLjA5NC0uMDQ1LS4xNzUtLjAwNy0uMjM3TC4zMjUgMTguNTgyek0xMyAxM1Y5aC0ydjRoMnoiLz4KICAgIDxjaXJjbGUgY3g9IjEyIiBjeT0iMTciIHI9IjEiLz4KICA8L2c+CiAgPGRlZnM+CiAgICA8Y2xpcFBhdGggaWQ9InRyaWFuZ2xlLXdhcm5pbmctcmVkX3N2Z19fY2xpcDAiPgogICAgICA8cGF0aCBmaWxsPSIjZmZmIiBkPSJNMCAwaDI0djI0SDB6Ii8+CiAgICA8L2NsaXBQYXRoPgogIDwvZGVmcz4KPC9zdmc+Cg==');
          background-position: calc(100% - 17px) 13px;
          background-repeat: no-repeat;

          &:focus {
            box-shadow: inset 0 0 0 2px var(--color-red);
          }
        }
        legend {
          color: var(--color-red);
        }
      }
    }

    .marketField {
      padding-bottom: var(--space-10);

      .CheckboxesFieldLabel {
        font-weight: var(--font-weight-medium);
        line-height: var(--line-height-24);
        font-size: var(--font-size-14);
      }
    }
  }
</style>
